import {
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
} from 'react';
import {
  Wrench,
  Plus,
  Search,
  Pencil,
  Trash2,
  X,
} from 'lucide-react';
import { Toast } from '../../components/ui/FormField';
import { StatsCard } from '../../components/ui/StatsCard';
import { SortHeader, makeSortToggle } from '../../components/ui/SortHeader';
import {
  PrimaryButton as FieldPrimary,
} from '../../components/ui/Field';
import { ToolModelForm, type ToolModelErrors } from '../../components/forms/ToolModelForm';
import { useCrudPage } from '../../hooks/useCrudPage';
import { usePageAction } from '../../stores/page-store';
import { useActivityStore } from '../../stores/activity-store';
import { useAuthStore } from '../../stores/auth-store';
import { toolModelApi, toolApi } from '../../services/api-services';
import type { ToolModel, Tool } from '../../types/api';

// ─────────────────────────────────────────────────────────────
// ToolModelsPage — Operations Deck redesign (Variation A)
//
// Layout follows Companies / Inventory / Locations / Users / Wells:
//   • 3-card stats strip (Total / In use / Recently added)
//   • Search box (with clear) + record count
//   • CSS-grid header with SortHeader cells (server-side sort)
//   • Per-row units chip (client-side computed from toolApi)
//   • Pagination footer
//   • ToolModelForm modal (Add / Edit) with field-level server errors
// ─────────────────────────────────────────────────────────────

// Sentinel for the client-side Units column. Mirrors SitesPage's
// SORT_WELLS pattern.
const SORT_UNITS = '__units__';

const COL_TEMPLATE =
  'minmax(220px, 2fr) minmax(170px, 1.3fr) minmax(260px, 2.2fr) 110px 130px';

export function ToolModelsPage() {
  const authUser = useAuthStore((s) => s.user);
  const canEdit = authUser?.role === 'SuperAdmin' || authUser?.role === 'CompanyAdmin';
  const username = authUser?.username;
  const recordActivity = useActivityStore((s) => s.record);

  const crud = useCrudPage<ToolModel>({
    fetchFn: toolModelApi.getAll,
    initialSortCol: 'ToolName',
  });
  const [saving, setSaving] = useState(false);
  const [serverFieldError, setServerFieldError] = useState<
    { field: keyof ToolModelErrors; message: string } | null
  >(null);

  // toolModelId → unit count, hydrated from a single tools fetch.
  // Done once on mount + after any create/update/delete.
  const [unitCounts, setUnitCounts] = useState<Record<number, number>>({});
  const [unitsLoaded, setUnitsLoaded] = useState(false);

  const loadUnits = async () => {
    try {
      const r = await toolApi.getAll({ limit: 5000 });
      const map: Record<number, number> = {};
      (r.entities ?? []).forEach((t: Tool) => {
        if (t.toolModelId != null) {
          map[t.toolModelId] = (map[t.toolModelId] ?? 0) + 1;
        }
      });
      setUnitCounts(map);
      setUnitsLoaded(true);
    } catch {
      setUnitsLoaded(true); // surface "—" instead of perpetual skeleton
    }
  };

  useEffect(() => {
    loadUnits();
  }, []);

  const handleSort = makeSortToggle(crud.sortCol, crud.sortDir, crud.setSortCol, crud.setSortDir);

  // Units column sorts client-side (the count is computed locally).
  // Other columns are sorted by the backend, so we just return data as-is.
  const sortedData = useMemo(() => {
    if (crud.sortCol !== SORT_UNITS) return crud.data;
    const dir = crud.sortDir === 'Ascending' ? 1 : -1;
    return [...crud.data].sort((a, b) => {
      const ua = a.id != null ? unitCounts[a.id] ?? 0 : 0;
      const ub = b.id != null ? unitCounts[b.id] ?? 0 : 0;
      return (ua - ub) * dir;
    });
  }, [crud.data, crud.sortCol, crud.sortDir, unitCounts]);

  // Stats — DB total + per-page derivations.
  const stats = useMemo(() => {
    const onPage = crud.data;
    const inUse = onPage.filter((m) => m.id != null && (unitCounts[m.id] ?? 0) > 0).length;
    const recentCutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
    const recent = onPage.filter((m) => {
      if (!m.createdDate) return false;
      const t = new Date(m.createdDate).getTime();
      return Number.isFinite(t) && t >= recentCutoff;
    }).length;
    return {
      total: crud.totalCount,
      inUse,
      recent,
    };
  }, [crud.data, crud.totalCount, unitCounts]);

  const totalPages = Math.max(1, Math.ceil(crud.totalCount / crud.pageSize));
  const currentPage = Math.floor(crud.offset / crud.pageSize) + 1;

  // ── CRUD ──
  const handleSave = async (form: Partial<ToolModel>) => {
    setSaving(true);
    setServerFieldError(null);
    try {
      const label = form.toolModelName?.trim() || 'Untitled model';
      const res = crud.editingItem
        ? await toolModelApi.update(form)
        : await toolModelApi.create(form);
      if (res?.status === 'Failed' || res?.error) {
        const msg = res?.error?.errorMessage || 'Failed to save tool model';
        // Surface "model number already exists" under the model code field.
        if (/model number|model code/i.test(msg)) {
          setServerFieldError({ field: 'model', message: msg });
        } else {
          crud.showToast(msg, 'error');
        }
        return;
      }
      crud.showToast(crud.editingItem ? 'Tool model updated' : 'Tool model created');
      recordActivity({
        type: crud.editingItem ? 'update' : 'create',
        entity: 'Tool models',
        label,
        user: username,
      });
      crud.closeModal();
      crud.load();
      loadUnits();
    } catch {
      crud.showToast('Failed to save tool model', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (m: ToolModel) => {
    const unitsForModel = m.id != null ? unitCounts[m.id] ?? 0 : 0;
    if (unitsForModel > 0) {
      crud.showToast(
        `This model has ${unitsForModel} unit${unitsForModel === 1 ? '' : 's'} in inventory. Remove or reassign tools before deleting.`,
        'error',
      );
      return;
    }
    if (!confirm(`Delete tool model "${m.toolModelName}"?`)) return;
    try {
      const res = await toolModelApi.delete(m.id);
      if (res?.status === 'Failed' || res?.error) {
        crud.showToast(res?.error?.errorMessage || 'Failed to delete tool model', 'error');
        return;
      }
      crud.showToast('Tool model deleted');
      recordActivity({
        type: 'delete',
        entity: 'Tool models',
        label: m.toolModelName?.trim() || `Model #${m.id}`,
        user: username,
      });
      crud.load();
      loadUnits();
    } catch {
      crud.showToast('Failed to delete tool model', 'error');
    }
  };

  // TopBar primary action.
  usePageAction(
    canEdit ? (
      <FieldPrimary onClick={crud.openCreate}>
        <Plus size={14} /> Add tool model
      </FieldPrimary>
    ) : null,
    [canEdit],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      {/* Stats strip */}
      <div className="grid gap-3 grid-cols-1 sm:grid-cols-3">
        <StatsCard
          label="Catalogue size"
          value={stats.total}
          sub="Models registered"
          accent="var(--color-accent)"
          loading={crud.loading}
        />
        <StatsCard
          label="In use (this page)"
          value={stats.inUse}
          sub="With at least one tool assigned"
          accent="var(--color-teal)"
          loading={crud.loading || !unitsLoaded}
        />
        <StatsCard
          label="Recently added"
          value={stats.recent}
          sub="Created in the last 30 days"
          accent="var(--color-warn)"
          loading={crud.loading}
        />
      </div>

      {/* Filter / search bar + grid */}
      <div
        style={{
          background: 'var(--color-panel)',
          border: '1px solid var(--color-border)',
          borderRadius: 10,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '14px 18px',
            borderBottom: '1px solid var(--color-border)',
            flexWrap: 'wrap',
          }}
        >
          {/* Search */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '7px 12px',
              background: 'var(--color-panel-alt)',
              border: '1px solid var(--color-border)',
              borderRadius: 8,
              width: 320,
              transition: 'border-color 180ms',
            }}
            className="focus-within:!border-[var(--color-accent-bright)]"
          >
            <Search size={15} style={{ color: 'var(--color-text-dim)' }} />
            <input
              placeholder="Search tool models…"
              value={crud.searchText}
              onChange={(e) => {
                crud.setSearchText(e.target.value);
                crud.setOffset(0);
              }}
              style={{
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontSize: 13.5,
                flex: 1,
                fontFamily: 'var(--font-body)',
                color: 'var(--color-text)',
                width: '100%',
              }}
            />
            {crud.searchText && (
              <button
                onClick={() => { crud.setSearchText(''); crud.setOffset(0); }}
                aria-label="Clear search"
                title="Clear search"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 18, height: 18, borderRadius: 9, padding: 0,
                  background: 'transparent', border: 'none',
                  color: 'var(--color-text-dim)', cursor: 'pointer', flexShrink: 0,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(15,23,32,0.08)';
                  e.currentTarget.style.color = 'var(--color-text)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = 'var(--color-text-dim)';
                }}
              >
                <X size={13} />
              </button>
            )}
          </div>

          <div style={{ flex: 1 }} />

          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--color-text-dim)' }}>
            {crud.loading
              ? 'Loading…'
              : `${crud.totalCount} model${crud.totalCount === 1 ? '' : 's'}`}
          </div>
        </div>

        {/* Header row */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: COL_TEMPLATE,
            padding: '12px 18px',
            borderBottom: '1px solid var(--color-border)',
            background: 'var(--color-panel-alt)',
            fontSize: 12.5,
            letterSpacing: 0.3,
            color: 'var(--color-text-mid)',
            fontWeight: 600,
            fontFamily: 'var(--font-body)',
          }}
        >
          <SortHeader label="Name" col="ToolName" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
          <SortHeader label="Model" col="Model" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
          <SortHeader label="Description" col="Description" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
          <SortHeader label="Units" col={SORT_UNITS} activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} align="right" />
          <div style={{ textAlign: 'right' }}>Actions</div>
        </div>

        {/* Rows */}
        {crud.loading ? (
          <div style={{ padding: 60, textAlign: 'center', color: 'var(--color-text-dim)', fontSize: 13 }}>
            Loading…
          </div>
        ) : sortedData.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: 'var(--color-text-dim)' }}>
            <Wrench size={32} style={{ margin: '0 auto 12px', opacity: 0.4 }} />
            <div style={{ fontSize: 14, marginBottom: 4 }}>
              {crud.searchText ? 'No tool models match your search' : 'No tool models yet'}
            </div>
            <div style={{ fontSize: 12 }}>
              {crud.searchText
                ? 'Try a different keyword or clear the search.'
                : canEdit
                  ? 'Add a tool model to the catalogue to get started.'
                  : 'Tool models defined by an administrator will appear here.'}
            </div>
          </div>
        ) : (
          <div>
            {sortedData.map((m, i) => (
              <ModelRow
                key={m.id ?? i}
                model={m}
                units={m.id != null ? unitCounts[m.id] : undefined}
                unitsLoaded={unitsLoaded}
                isLast={i === sortedData.length - 1}
                canEdit={canEdit}
                onEdit={() => crud.openEdit(m)}
                onDelete={() => handleDelete(m)}
              />
            ))}
          </div>
        )}

        {/* Pagination footer */}
        <div
          style={{
            padding: '12px 18px',
            borderTop: '1px solid var(--color-border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: 12,
            color: 'var(--color-text-dim)',
            background: 'var(--color-panel-alt)',
          }}
        >
          <div>
            Showing{' '}
            <span style={{ color: 'var(--color-text-mid)', fontWeight: 500 }}>{sortedData.length}</span>{' '}
            of{' '}
            <span style={{ color: 'var(--color-text-mid)', fontWeight: 500 }}>{crud.totalCount}</span>{' '}
            model{crud.totalCount === 1 ? '' : 's'}
          </div>
          {totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-mono)' }}>
              <PaginationButton
                onClick={() => crud.setOffset(Math.max(0, crud.offset - crud.pageSize))}
                disabled={crud.offset === 0}
              >
                ←
              </PaginationButton>
              <span style={{ padding: '0 10px', color: 'var(--color-text-mid)' }}>
                {currentPage} / {totalPages}
              </span>
              <PaginationButton
                onClick={() => crud.setOffset(crud.offset + crud.pageSize)}
                disabled={currentPage >= totalPages}
              >
                →
              </PaginationButton>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit modal */}
      <ToolModelForm
        open={crud.modalOpen}
        editing={crud.editingItem}
        saving={saving}
        serverFieldError={serverFieldError}
        onCancel={() => { setServerFieldError(null); crud.closeModal(); }}
        onSave={handleSave}
      />

      {crud.toast && (
        <Toast
          message={crud.toast.message}
          type={crud.toast.type}
          onClose={() => crud.setToast(null)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Row + helpers
// ─────────────────────────────────────────────────────────────

function ModelRow({
  model: m,
  units,
  unitsLoaded,
  isLast,
  canEdit,
  onEdit,
  onDelete,
}: {
  model: ToolModel;
  units: number | undefined;
  unitsLoaded: boolean;
  isLast: boolean;
  canEdit: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'grid',
        gridTemplateColumns: COL_TEMPLATE,
        padding: '13px 18px',
        borderBottom: isLast ? 'none' : '1px solid var(--color-border)',
        alignItems: 'center',
        fontSize: 14,
        background: hover ? 'var(--color-panel-alt)' : 'transparent',
        transition: 'background 120ms',
      }}
    >
      {/* Name */}
      <div
        style={{
          color: 'var(--color-text)',
          fontWeight: 500,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          minWidth: 0,
        }}
        title={m.toolModelName ?? ''}
      >
        {m.toolModelName || '—'}
      </div>

      {/* Model code */}
      <Cell mono accent ellipsis title={m.model ?? ''}>
        {m.model || '—'}
      </Cell>

      {/* Description */}
      <Cell dim ellipsis title={m.description ?? ''}>
        {m.description || '—'}
      </Cell>

      {/* Units chip */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <UnitsChip count={units} loaded={unitsLoaded} />
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
        {canEdit && (
          <>
            <IconButton title="Edit" onClick={onEdit}>
              <Pencil size={15} />
            </IconButton>
            <IconButton title="Delete" onClick={onDelete} hoverDanger>
              <Trash2 size={15} />
            </IconButton>
          </>
        )}
      </div>
    </div>
  );
}

function UnitsChip({ count, loaded }: { count: number | undefined; loaded: boolean }) {
  if (!loaded || count === undefined) {
    return (
      <span
        style={{
          display: 'inline-block',
          width: 28,
          height: 18,
          borderRadius: 4,
          background: 'var(--color-panel-alt)',
          opacity: 0.6,
        }}
      />
    );
  }
  const isZero = count === 0;
  return (
    <span
      title={`${count} unit${count === 1 ? '' : 's'} in inventory`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '3px 9px',
        borderRadius: 12,
        fontFamily: 'var(--font-mono)',
        fontSize: 12.5,
        fontWeight: 600,
        lineHeight: 1,
        border: '1px solid',
        background: isZero ? 'var(--color-panel-alt)' : 'rgba(11,74,111,0.05)',
        color: isZero ? 'var(--color-text-dim)' : 'var(--color-accent)',
        borderColor: isZero ? 'var(--color-border)' : 'rgba(11,74,111,0.25)',
      }}
    >
      {count}
    </span>
  );
}

function Cell({
  children,
  mono = false,
  dim = false,
  accent = false,
  ellipsis = false,
  style,
  title,
}: {
  children: React.ReactNode;
  mono?: boolean;
  dim?: boolean;
  accent?: boolean;
  ellipsis?: boolean;
  style?: CSSProperties;
  title?: string;
}) {
  return (
    <div
      title={title}
      style={{
        fontFamily: mono ? 'var(--font-mono)' : 'var(--font-body)',
        color: accent
          ? 'var(--color-accent)'
          : dim
            ? 'var(--color-text-mid)'
            : 'var(--color-text)',
        fontSize: mono ? 13 : 14,
        whiteSpace: ellipsis ? 'nowrap' : undefined,
        overflow: ellipsis ? 'hidden' : undefined,
        textOverflow: ellipsis ? 'ellipsis' : undefined,
        minWidth: 0,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function IconButton({
  children,
  onClick,
  title,
  hoverDanger,
}: {
  children: React.ReactNode;
  onClick: () => void;
  title: string;
  hoverDanger?: boolean;
}) {
  const [hover, setHover] = useState(false);
  const idleBg = 'var(--color-panel)';
  const idleBorder = 'var(--color-border)';
  const idleColor = hoverDanger ? '#a14a47' : 'var(--color-accent)';
  const hoverBg = hoverDanger ? 'rgba(185,28,28,0.08)' : 'rgba(11,74,111,0.08)';
  const hoverBorder = hoverDanger ? 'rgba(185,28,28,0.35)' : 'var(--color-accent)';
  const hoverColor = hoverDanger ? 'var(--color-bad)' : 'var(--color-accent-hover)';
  const active = hover;
  return (
    <button
      title={title}
      aria-label={title}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: 32,
        height: 32,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 7,
        border: `1px solid ${active ? hoverBorder : idleBorder}`,
        cursor: 'pointer',
        background: active ? hoverBg : idleBg,
        color: active ? hoverColor : idleColor,
        transition: 'all 120ms',
        transform: active ? 'translateY(-1px)' : 'none',
        boxShadow: active ? '0 2px 6px rgba(15,23,32,0.08)' : 'none',
      }}
    >
      {children}
    </button>
  );
}

function PaginationButton({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        width: 26,
        height: 26,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid var(--color-border)',
        background: 'var(--color-panel)',
        color: disabled ? 'var(--color-text-dim)' : 'var(--color-text-mid)',
        borderRadius: 4,
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: 12,
        opacity: disabled ? 0.4 : 1,
      }}
    >
      {children}
    </button>
  );
}
